import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import InputMask from 'react-input-mask';
import Header from '../../components/Header';
import api from '../../services/api';
import { showError, showSuccess } from '../../services/alert';

export default function FornecedorForm() {
  const { id } = useParams();
  const isEdit = Boolean(id && id !== 'novo');
  const navigate = useNavigate();

  const onlyDigits = (v) => (v || '').toString().replace(/\D/g, '');

  const [fornecedor, setFornecedor] = useState({
    nome: '',
    documento: '',
    telefone: '',
    email: '',
    endereco: '',

    // ===== NOVOS CAMPOS NF-e (Destinatário) =====
    inscricaoEstadual: '',
    indicadorIe: 9,

    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    uf: '',
    municipio: '',
    codigoMunicipioIbge: '',

    codigoPais: '1058',
    nomePais: 'BRASIL'
  });

  const [docType, setDocType] = useState('cpf');
  const mask = docType === 'cpf' ? '999.999.999-99' : '99.999.999/9999-99';

  useEffect(() => {
    if (isEdit) {
      api.get(`/fornecedor/${id}`)
        .then(res => {
          const data = res.data || {};
          const numbers = (data.documento || '').replace(/\D/g, '');
          setDocType(numbers.length === 11 ? 'cpf' : 'cnpj');

          setFornecedor(prev => ({
            ...prev,
            ...data,
            indicadorIe: data.indicadorIe ?? 9,
            codigoPais: data.codigoPais || '1058',
            nomePais: data.nomePais || 'BRASIL'
          }));
        })
        .catch(() => showError('Erro ao carregar fornecedor.'));
    }
  }, [id, isEdit]);

  const handleChange = e => {
    const { name, value } = e.target;

    // Normalizações leves sem quebrar seu padrão
    if (name === 'cep') {
      const v = onlyDigits(value).slice(0, 8);
      setFornecedor(p => ({ ...p, [name]: v }));
      return;
    }

    if (name === 'codigoMunicipioIbge') {
      const v = onlyDigits(value).slice(0, 7);
      setFornecedor(p => ({ ...p, [name]: v }));
      return;
    }

    if (name === 'uf') {
      const v = (value || '').toUpperCase().slice(0, 2);
      setFornecedor(p => ({ ...p, [name]: v }));
      return;
    }

    setFornecedor(p => ({ ...p, [name]: value }));
  };

  const handleTypeChange = e => {
    setDocType(e.target.value);
    setFornecedor(p => ({ ...p, documento: '' }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const payload = {
        ...fornecedor,
        documento: onlyDigits(fornecedor.documento),

        cep: onlyDigits(fornecedor.cep).slice(0, 8),
        codigoMunicipioIbge: onlyDigits(fornecedor.codigoMunicipioIbge).slice(0, 7),

        indicadorIe: Number(fornecedor.indicadorIe ?? 9),
        codigoPais: '1058',
        nomePais: 'BRASIL'
      };

      const res = isEdit
        ? await api.put(`/fornecedor/${id}`, payload)
        : await api.post('/fornecedor', payload);

      showSuccess(res.data?.message || 'Operação realizada com sucesso.');
      navigate('/fornecedor');
    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao salvar fornecedor.');
    }
  };

  return (
    <div className="container mt-4">
      <Header />
      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex align-items-center gap-3">
          <button className="btn btn-light btn-sm" type="button" onClick={() => navigate('/fornecedor')}>
            <i className="bi bi-arrow-left text-success"></i> Voltar
          </button>
          <h5 className="mb-0">{isEdit ? 'Editar Fornecedor' : 'Cadastrar Fornecedor'}</h5>
        </div>

        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-6">
              <label>Nome</label>
              <input
                name="nome"
                value={fornecedor.nome}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="col-md-3">
              <label>Tipo Documento</label>
              <select className="form-select" value={docType} onChange={handleTypeChange}>
                <option value="cpf">CPF</option>
                <option value="cnpj">CNPJ</option>
              </select>
            </div>

            <div className="col-md-3">
              <label>Documento</label>
              <InputMask mask={mask} value={fornecedor.documento} onChange={handleChange} maskPlaceholder={null}>
                {() => <input name="documento" className="form-control" required />}
              </InputMask>
            </div>

            <div className="col-md-6">
              <label>Telefone</label>
              <InputMask
                mask="(99) 99999-9999"
                value={fornecedor.telefone}
                onChange={handleChange}
                maskPlaceholder={null}
              >
                {() => <input name="telefone" className="form-control" />}
              </InputMask>
            </div>

            <div className="col-md-6">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={fornecedor.email}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-12">
              <label>Endereço (texto antigo)</label>
              <input
                name="endereco"
                value={fornecedor.endereco}
                onChange={handleChange}
                className="form-control"
              />
              <small className="text-muted">
                Mantido para compatibilidade. A NF-e usa os campos abaixo.
              </small>
            </div>

            <hr className="my-2" />

            <div className="col-12">
              <h6 className="text-success fw-semibold mb-0">Dados NF-e (Destinatário)</h6>
              <small className="text-muted">
                Preencha para evitar rejeições (enderDest + indIEDest).
              </small>
            </div>

            <div className="col-md-4">
              <label>Inscrição Estadual (IE)</label>
              <input
                name="inscricaoEstadual"
                value={fornecedor.inscricaoEstadual || ''}
                onChange={handleChange}
                className="form-control"
                placeholder="Opcional / obrigatório se contribuinte"
              />
            </div>

            <div className="col-md-4">
              <label>Indicador IE (indIEDest)</label>
              <select
                className="form-select"
                name="indicadorIe"
                value={fornecedor.indicadorIe ?? 9}
                onChange={handleChange}
              >
                <option value={1}>1 - Contribuinte ICMS</option>
                <option value={2}>2 - Contribuinte Isento</option>
                <option value={9}>9 - Não Contribuinte</option>
              </select>
            </div>

            <div className="col-md-4">
              <label>CEP</label>
              <InputMask
                mask="99999-999"
                value={fornecedor.cep || ''}
                onChange={(e) => {
                  const raw = onlyDigits(e.target.value);
                  setFornecedor(p => ({ ...p, cep: raw.slice(0, 8) }));
                }}
                maskPlaceholder={null}
              >
                {() => (
                  <input
                    name="cep"
                    className="form-control"
                    placeholder="Somente números"
                  />
                )}
              </InputMask>
            </div>

            <div className="col-md-6">
              <label>Logradouro</label>
              <input
                name="logradouro"
                value={fornecedor.logradouro || ''}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-2">
              <label>Número</label>
              <input
                name="numero"
                value={fornecedor.numero || ''}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-4">
              <label>Complemento</label>
              <input
                name="complemento"
                value={fornecedor.complemento || ''}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-4">
              <label>Bairro</label>
              <input
                name="bairro"
                value={fornecedor.bairro || ''}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-2">
              <label>UF</label>
              <input
                name="uf"
                value={fornecedor.uf || ''}
                onChange={handleChange}
                className="form-control"
                placeholder="Ex: MT"
              />
            </div>

            <div className="col-md-6">
              <label>Município</label>
              <input
                name="municipio"
                value={fornecedor.municipio || ''}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-4">
              <label>Código IBGE Município (cMun)</label>
              <input
                name="codigoMunicipioIbge"
                value={fornecedor.codigoMunicipioIbge || ''}
                onChange={handleChange}
                className="form-control"
                placeholder="7 dígitos (ex: 5103403)"
              />
            </div>

            <div className="col-12 d-flex justify-content-end">
              <button type="submit" className="btn btn-success me-2">Salvar</button>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/fornecedor')}>
                Cancelar
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}
